import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { Flex, Box } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import {
  Heading, Row, Section, Stepper, Loading, Title, Subtitle, Link, NavigationLink,
} from '../components';
import { ServerToServerToggle } from '../sections';

import api from '../api';
import { useAppState, useNotification } from '../hooks';

const LINK_TO_DOCS = 'https://developers.liveperson.com/maven-context-warehouse-inbox-system-attributes.html';

function ContextPage(props) {
  const {
    name, type, subtitle,
  } = props;
  const isMounted = useRef(true);
  const [contexts, setContexts] = useState([]);
  const [isFetchingContexts, setIsFetchingContexts] = useState(null);
  const { isCcsV2, accountId: brandId, themeMode } = useAppState();
  const { addNotification } = useNotification();

  const getContextData = useCallback(async () => {
    if (!isFetchingContexts) {
      if (isMounted.current) {
        setIsFetchingContexts(true);
      }
      try {
        const data = isCcsV2
          ? await api.ccsV2.getSystemContext(brandId, type)
          : await api.mavenContext.getSystemContext(brandId, type);

        if (isMounted.current) {
          setContexts(data);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingContexts(false);
      }
    }
  }, [addNotification, brandId, isFetchingContexts, type, isCcsV2]);

  useEffect(() => {
    async function fetchData() {
      await getContextData();
    }
    fetchData();
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  return (
    <Box>
      <Heading>{name}</Heading>
      <Subtitle mb={3}>
        The Inbox Conversational attributes allows you to use attributes such as Last Agent Skill to
        build customized conversation solutions.
      </Subtitle>
      <Section
        section="context-getting-started"
        title="Getting started"
        subtitle={(
          <Box>
            Using the Conversation Context Service with Dynamic Routing requires configuring several
            different features. To complete the remaining steps, please see the
            {' '}
            <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
              documentation
            </Link>
            .
          </Box>
        )}
      >
        <Stepper
          steps={[
            { id: 1, title: 'Manage attributes in Conversation Context Service' },
            {
              id: 2,
              title: <NavigationLink to="/intent-content-policies">Build routing policies</NavigationLink>,
            },
            {
              id: 3,
              title: (
                <Box>
                  Connect to
                  {' '}
                  <NavigationLink to="/ask-maven">Orchestrator</NavigationLink>
                </Box>
              ),
            },
            { id: 4, title: 'Start using policies' },
          ]}
          activeStep={1}
        />
      </Section>
      {
        isCcsV2 ? null
          : (
            <ServerToServerToggle />
          )
      }
      <Section title={`${name} context attributes`} subtitle={subtitle} mb={3}>
        <Title fontSize={1} mb={2}>
          Click to copy an attribute
        </Title>
        {
          isFetchingContexts !== false
            ? (
              <Flex align="center" justifyContent="center" py={3}>
                <Loading size={24} />
              </Flex>
            ) : (
              contexts.map((context, index) => (
                <Row
                  themeMode={themeMode}
                  key={context}
                  data={{ name: context }}
                  rowType="list"
                  isLast={index + 1 === contexts.length}
                  enableCopy
                />
              ))
            )
        }
      </Section>
    </Box>
  );
}

ContextPage.defaultProps = {
  subtitle: undefined,
};

ContextPage.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  type: PropTypes.string.isRequired,
};

export default ContextPage;
